<template>
    <div class="city-sales-selector">
        <!-- 按区域-经营城市展示已选城市 -->
        <mc-panel
            class="city-display-panel"
        >
            <template slot="title">
                <div class="panel-header-wrapper">
                    <span class="panel-title">已选城市</span>
                </div>
            </template>
            <template slot="operator">
                <basic-plan-city-select
                    v-show="isCitySelectorVisible"
                    ref="basicPlanCitySelect"
                    :value="selectedCityIdsForBasicPlan"
                    :data="areaConfigListForBasicPlan"
                    :disabled="disabled"
                    :disabled-city-ids="disabledIds"
                    @change="handleBasicPlanCityChange"
                />
                <mc-button
                    v-show="!isCitySelectorVisible"
                    type="primary"
                    size="small"
                    class="add-city-btn"
                    @click="handleAddCityNonExpandMode"
                >
                    增加城市
                </mc-button>
            </template>
            <div
                v-for="region in groupedCitiesByRegion"
                :key="region.regionId"
                class="region-group"
            >
                <div class="region-header">
                    <span class="region-name">{{ region.regionLabel }}</span>
                    <span class="region-count">({{ region.manageCities.length }}/{{ region.totalCityCount }})</span>
                </div>
                <div class="manage-cities-list">
                    <mc-tag
                        v-for="manageCity in region.manageCities"
                        :key="manageCity.id"
                    >
                        {{ manageCity.label }}
                        <i
                            class="el-icon-close"
                            @click="removeCity(manageCity.id)"
                        ></i>
                    </mc-tag>
                </div>
            </div>
        </mc-panel>
    </div>
</template>

<script>
import BasicPlanCitySelect from '../BasicPlanCitySelect';

export default {
    name: 'CitySalesSelector',
    components: {
        BasicPlanCitySelect,
    },
    props: {
        // v-model 绑定的选中城市ID列表
        value: {
            type: Array,
            default: () => [],
        },
        // 业态类型列表
        ytTypes: {
            type: Array,
            default: () => [],
        },
        // 城市数据源（区域+经营城市的结构）
        data: {
            type: Array,
            default: () => [],
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: () => false,
        },
        // 禁用的城市ID列表
        disabledIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            manageCityList: [], // 经营城市列表（包含所有层级信息：regionId、regionLabel、basicCityId、basicCityLabel）
            dataPollingTimer: null, // 数据轮询定时器
            isDataLoaded: false, // 数据是否已加载
            isCitySelectorVisible: false, // 非扩城场景下的城市选择器显隐状态
        };
    },
    computed: {
        // 为 BasicPlanCitySelect 组件准备的选中城市ID列表
        selectedCityIdsForBasicPlan() {
            // 直接使用当前选中的城市ID，因为 BasicPlanCitySelect 使用相同的格式
            return this.value || [];
        },
        // 为 BasicPlanCitySelect 组件准备的数据源（需要包装成数组格式）
        areaConfigListForBasicPlan() {
            // 将数据转换为 BasicPlanCitySelect 需要的格式
            // this.data 已经是 areaConfigList，需要包装回业态维度格式：[{areaConfigList, ...}]
            return this.data ? this.data : [];
        },
        // 按区域-经营城市分组已选城市
        groupedCitiesByRegion() {
            if (!this.value || !this.value.length) {
                return [];
            }

            // 从 manageCityList 中查找已选的城市
            const selectedCities = this.manageCityList.filter((city) => {
                return this.value.includes(city.id);
            });

            // 如果没有已选城市，则返回空
            if (!selectedCities.length) {
                return [];
            }

            // 计算各区域的总城市数
            const regionTotals = {};
            this.manageCityList.forEach((city) => {
                const regionId = city.regionId;
                regionTotals[regionId] = (regionTotals[regionId] || 0) + 1;
            });

            // 按区域分组
            const grouped = {};
            selectedCities.forEach((city) => {
                const regionId = city.regionId;
                if (!grouped[regionId]) {
                    grouped[regionId] = {
                        regionId,
                        regionLabel: city.regionLabel,
                        manageCities: [],
                        totalCityCount: regionTotals[regionId] || 0,
                    };
                }
                grouped[regionId].manageCities.push(city);
            });

            // 对经营城市进行排序：主城在前，剩余城市按城市id升序
            Object.values(grouped).forEach((region) => {
                region.manageCities.sort((a, b) => {
                    // 主城（type === 1）排在前面
                    if (a.type === 1 && b.type !== 1) return -1;
                    if (a.type !== 1 && b.type === 1) return 1;

                    // 如果都是主城或都不是主城，则按城市id升序排序
                    const cityIdA = Number(a.cityId) || 0;
                    const cityIdB = Number(b.cityId) || 0;
                    return cityIdA - cityIdB;
                });
            });

            // 转换为数组格式
            return Object.values(grouped);
        },
    },
    watch: {
        // 监听基盘城市组件的数据变化
        manageCityList: {
            handler(newData) {
                if (newData && newData.length > 0) {
                    this.isDataLoaded = true;
                }
            },
            deep: true,
        },
    },
    mounted() {
        // 组件挂载后，启动定时器监听数据加载
        this.startDataPolling();
    },
    beforeDestroy() {
        // 组件销毁前清理定时器
        if (this.dataPollingTimer) {
            clearInterval(this.dataPollingTimer);
        }
    },
    methods: {
        /**
         * 处理基盘城市选择器变化
         * @param {Array} selectedCityIds - 选中的城市ID列表
         */
        handleBasicPlanCityChange(selectedCityIds) {
            // 将基盘城市选择器的选择同步到主选择器
            this.$emit('input', selectedCityIds);
            this.$emit('change', selectedCityIds);
        },
        /**
         * 非扩城模式下：点击"增加城市"按钮
         */
        handleAddCityNonExpandMode() {
            this.isCitySelectorVisible = true;
        },
        /**
         * 删除单个城市
         * @param {String} cityId - 城市ID
         */
        removeCity(cityId) {
            const newValue = this.value.filter((id) => id !== cityId);
            this.$emit('input', newValue);
            this.$emit('change', newValue);
        },
        /**
         * 启动数据轮询，定期检查基盘城市组件的数据是否加载完成
         */
        startDataPolling() {
            // 清除可能存在的旧定时器
            if (this.dataPollingTimer) {
                clearInterval(this.dataPollingTimer);
            }

            // 立即尝试提取一次数据
            this.$nextTick(() => {
                this.extractCityData();
            });

            // 设置定时器，每 200ms 检查一次数据是否加载完成
            this.dataPollingTimer = setInterval(() => {
                if (!this.isDataLoaded && this.$refs.basicPlanCitySelect) {
                    this.extractCityData();
                }
                // 如果数据已加载或超过 5 秒，停止轮询
                if (this.isDataLoaded) {
                    clearInterval(this.dataPollingTimer);
                    this.dataPollingTimer = null;
                }
            }, 200);

            // 5 秒后停止轮询（防止永久轮询）
            setTimeout(() => {
                if (this.dataPollingTimer) {
                    clearInterval(this.dataPollingTimer);
                    this.dataPollingTimer = null;
                }
            }, 5000);
        },
        /**
         * 从 BasicPlanCitySelect 组件中提取城市数据
         * 将树形数据扁平化为区域、经营城市列表
         */
        extractCityData() {
            if (!this.$refs.basicPlanCitySelect) {
                return;
            }

            const innerData = this.$refs.basicPlanCitySelect.innerData || [];

            // 如果数据为空，不进行处理
            if (!innerData.length) {
                return;
            }

            const manageCities = [];

            // 遍历树形数据，提取经营城市信息
            // 结构：region -> manageCity (已去掉基盘城市层级)
            innerData.forEach((region) => {
                // 遍历经营城市
                (region.children || []).forEach((manageCity) => {
                    // 提取区域ID（格式：area_${areaId}）
                    const areaId = region.id.replace(/^area_/, '');
                    manageCities.push({
                        id: manageCity.id,
                        label: manageCity.label,
                        cityStatus: manageCity.cityStatus,
                        type: manageCity.type,
                        cityId: manageCity.cityId,
                        disabled: manageCity.disabled,
                        regionId: areaId,
                        regionLabel: region.label,
                    });
                });
            });

            this.manageCityList = manageCities;
        },
    },
};
</script>

<style lang="scss" scoped>
.city-sales-selector {
    .city-display-panel {
        ::v-deep .mc-panel-body {
            padding: 16px;
        }

        .panel-header-wrapper {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            gap: 16px;

            .panel-title {
                font-size: 14px;
                font-weight: 500;
                color: #333;
                flex-shrink: 0;
            }

            .city-selector-wrapper {
                flex: 1;
                min-width: 0;
            }
        }

        .region-group {
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        .region-header {
            display: flex;
            align-items: center;
            margin-bottom: 12px;

            .region-name {
                font-weight: 500;
                font-size: 14px;
            }

            .region-count {
                margin-left: 8px;
                color: #999;
                font-size: 12px;
            }
        }

        .manage-cities-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;

            ::v-deep .mc-tag {
                margin: 0 !important;
                display: flex;
                align-items: center;
                gap: 4px;

                .el-icon-close {
                    color: #999;
                    cursor: pointer;
                    font-size: 12px;
                    margin-left: 4px;

                    &:hover {
                        color: #333;
                    }
                }
            }
        }
    }
}
</style>
